import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useApi from '../../helpers/TerserahApi';
import chatSocketService from '../../helpers/ChatSocket';
import Cookies from 'js-cookie';

const NotificationCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    // Reutiliza el socket único de la app: toma el token de las cookies
    // y lo refresca automáticamente (no se abre una conexión propia).
    const token = Cookies.get('token');
    if (!token) return;

    socketRef.current = chatSocketService.connect(token);

    const handleNotification = (notification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
    };

    chatSocketService.on('notification', handleNotification);
    chatSocketService.on('notification:new', handleNotification);

    fetchNotifications();
    fetchUnreadCount();

    return () => {
      chatSocketService.off('notification', handleNotification);
      chatSocketService.off('notification:new', handleNotification);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const data = await api.get('/notifications?limit=20');
      const list =
        data?.notifications ?? data?.data?.notifications ?? (Array.isArray(data) ? data : []);
      setNotifications(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const data = await api.get('/notifications/unread-count');
      setUnreadCount(data?.count ?? data?.unreadCount ?? 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read
      if (!notification.read) {
        await api.patch(`/notifications/${notification.public_id}/read`);
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.public_id === notification.public_id
              ? { ...n, read: true }
              : n
          )
        );
      }

      // Navigate to action URL
      if (notification.action_url) {
        navigate(notification.action_url);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await api.patch('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(prev =>
        prev.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      message: '💬',
      offer_received: '💰',
      offer_accepted: '✅',
      offer_rejected: '❌',
      offer_countered: '🔄',
      review_received: '⭐',
      ad_sold: '🎉',
      ad_expired: '⏰',
      price_drop: '📉',
      saved_search_match: '🔔',
      follow_new_ad: '👀'
    };
    return icons[type] || '📬';
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });
    
    return notifDate.toLocaleDateString();
  };

  return (
    <Container ref={dropdownRef}>
      <BellButton onClick={handleToggle}>
        🔔
        {unreadCount > 0 && <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>}
      </BellButton>

      {isOpen && (
        <Dropdown>
          <DropdownHeader>
            <HeaderTitle>{t('notifications.title')}</HeaderTitle>
            {unreadCount > 0 && (
              <MarkAllReadButton onClick={handleMarkAllAsRead}>
                {t('notifications.markAllRead')}
              </MarkAllReadButton>
            )}
          </DropdownHeader>

          <NotificationList>
            {loading ? (
              <LoadingMessage>{t('common.loading')}</LoadingMessage>
            ) : notifications.length === 0 ? (
              <EmptyMessage>{t('notifications.empty')}</EmptyMessage>
            ) : (
              notifications.map(notification => (
                <NotificationItem
                  key={notification.public_id}
                  onClick={() => handleNotificationClick(notification)}
                  $isUnread={!notification.read}
                >
                  <NotificationIcon>
                    {getNotificationIcon(notification.type)}
                  </NotificationIcon>
                  <NotificationContent>
                    <NotificationTitle>{notification.title}</NotificationTitle>
                    <NotificationMessage>{notification.message}</NotificationMessage>
                    <NotificationTime>{formatTime(notification.createdAt)}</NotificationTime>
                  </NotificationContent>
                  {!notification.read && <UnreadDot />}
                </NotificationItem>
              ))
            )}
          </NotificationList>
        </Dropdown>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const BellButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  position: relative;
  padding: 8px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.1);
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  width: 380px;
  max-height: 500px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 320px;
    right: -50px;
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const MarkAllReadButton = styled.button`
  background: transparent;
  border: none;
  color: #4caf50;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }
`;

const NotificationList = styled.div`
  overflow-y: auto;
  max-height: 440px;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.2s;
  border-bottom: 1px solid #f0f0f0;
  background: ${props => props.$isUnread ? '#f0f8f0' : 'white'};
  position: relative;

  &:hover {
    background: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NotificationIcon = styled.div`
  font-size: 24px;
  margin-right: 12px;
  flex-shrink: 0;
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const NotificationMessage = styled.div`
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const NotificationTime = styled.div`
  font-size: 12px;
  color: #999;
`;

const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
`;

const LoadingMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
`;

const EmptyMessage = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #999;
`;

export default NotificationCenter;
